import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle, Package, Clock, Printer, Home,
  DollarSign, Loader2, CreditCard, Building2, Banknote, Copy, Truck
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Dynamic settings will be fetched

export const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cart');
  const orderId = searchParams.get('order');

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('moncash');
  const [paymentInstructions, setPaymentInstructions] = useState(null);
  const [orderCreating, setOrderCreating] = useState(false); // Prevent double creation

  // Shipping options
  const [globalSettings, setGlobalSettings] = useState(null);
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const [selectedShippingCompanyId, setSelectedShippingCompanyId] = useState("");
  const [shippingLbs, setShippingLbs] = useState(0);
  const [payShippingNow, setPayShippingNow] = useState(true);
  const [showShippingForm, setShowShippingForm] = useState(true);

  const token = localStorage.getItem('enterprise_token');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }

    if (cartId) {
      fetchCart();
    } else if (orderId) {
      fetchOrder();
    } else {
      setLoading(false);
    }
    fetchGlobalSettings();
    fetchShippingCompanies();
  }, [token, cartId, orderId, navigate]);

  const fetchGlobalSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchShippingCompanies = async () => {
    try {
      const response = await axios.get(`${API}/shipping`);
      setShippingCompanies(response.data);
    } catch (error) {
      console.error("Error fetching shipping:", error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/punchout/cart/${cartId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.session?.cart_data) {
        setCartData(response.data.session.cart_data);
        // We don't auto-create here anymore, wait for shipping selection
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data.order);
    } catch (error) {
      console.error("Error fetching order:", error);
    } finally {
      setLoading(false);
    }
  };

  const createOrderFromCart = async (cart) => {
    // Prevent double creation
    if (orderCreating || order) {
      return;
    }

    setOrderCreating(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders`,
        {
          items: cart.items,
          notes: "Kòmand depi Amazon Business Punchout",
          shipping_company_id: selectedShippingCompanyId,
          shipping_lbs: parseFloat(shippingLbs),
          pay_shipping_now: payShippingNow
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setOrder(response.data.order);
      }
    } catch (error) {
      console.error("Error creating order:", error);
    } finally {
      setOrderCreating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePayment = async () => {
    if (!order) return;

    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${order.id}/pay`,
        { method: selectedPaymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        if (selectedPaymentMethod === 'moncash' && response.data.redirect_url) {
          toast.success("Redireksyon nan MonCash...");
          window.location.href = response.data.redirect_url;
        } else {
          // Virement or Zelle - show instructions
          setPaymentInstructions(response.data.instructions);
          toast.success(`Tanpri swiv enstriksyon ${selectedPaymentMethod} yo`);
          setOrder(prev => ({ ...prev, status: "pending_payment" }));
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Erè nan peman an");
    } finally {
      setPaymentLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopye!");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatHTG = (amount) => {
    return new Intl.NumberFormat('fr-HT').format(amount || 0) + ' HTG';
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: { bg: "bg-yellow-100", text: "text-yellow-800", label: "AN ATANT APWOBASYON" },
      approved: { bg: "bg-green-100", text: "text-green-800", label: "APWOUVE - PRÈ POU PEYE" },
      pending_payment: { bg: "bg-orange-100", text: "text-orange-800", label: "AP TANN PEMAN" },
      paid: { bg: "bg-blue-100", text: "text-blue-800", label: "PEYE" },
      rejected: { bg: "bg-red-100", text: "text-red-800", label: "REJTE" },
      completed: { bg: "bg-blue-100", text: "text-blue-800", label: "KONPLÈ" }
    };
    const s = styles[status] || styles.pending_approval;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-bold ${s.bg} ${s.text}`}>
        {s.label}
      </span>
    );
  };

  // Calculate pricing
  const getTotals = () => {
    const items = order?.items || cartData?.items || [];
    const amazonSubtotal = order?.amazon_subtotal || items.reduce((sum, item) => sum + (item.total || item.quantity * item.unit_price), 0);

    const fee_percent = globalSettings?.bvglinkamazon_fee_percent || 0.10;
    const bvgpromoFee = order?.bvgpromo_fee || (amazonSubtotal * fee_percent);

    const base_total_usd = amazonSubtotal + bvgpromoFee;

    let haiti_shipping_fee = 0;
    if (order?.haiti_shipping_fee) {
      haiti_shipping_fee = order.haiti_shipping_fee;
    } else {
      const selectedCompany = shippingCompanies.find(c => c.id === selectedShippingCompanyId);
      if (selectedCompany && shippingLbs > 0) {
        haiti_shipping_fee = selectedCompany.price_per_lbs * shippingLbs;
      }
    }

    const pay_now = order ? order.pay_shipping_now : payShippingNow;
    const totalUSD = pay_now ? (base_total_usd + haiti_shipping_fee) : base_total_usd;
    const rate = order?.exchange_rate || globalSettings?.usd_to_htg_rate || 148;
    const totalHTG = totalUSD * rate;

    return { amazonSubtotal, bvgpromoFee, fee_percent, base_total_usd, haiti_shipping_fee, totalUSD, totalHTG, rate, pay_now };
  };

  const totals = getTotals();

  const canPay = order?.status === 'approved' || order?.status === 'pending_payment';

  return (
    <EnterpriseLayout>
      <div className="max-w-2xl mx-auto space-y-6 py-8" data-testid="order-confirmation">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF9900]" />
          </div>
        ) : (
          <>
            {/* Success Header */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#232F3E] mb-2">Kòmand Konfime!</h1>

              {order && (
                <div className="flex items-center justify-center gap-3 mt-4">
                  <span className="text-gray-600">Nimewo Kòmand:</span>
                  <span className="font-mono font-bold text-[#232F3E]">{order.order_number}</span>
                  {getStatusBadge(order.status)}
                </div>
              )}
            </div>

            {/* Cart Summary */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-[#232F3E] mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#FF9900]" />
                  Rezime Panye
                </h2>

                <div className="space-y-4">
                  {items.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Pa gen atik</p>
                  ) : (
                    items.map((item, index) => (
                      <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center border">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-[#232F3E] line-clamp-2">
                            {item.name || item.description || 'Pwodwi Amazon'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            ASIN: {item.asin || item.supplier_part_id || 'N/A'}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-gray-600">
                              Kantite: {item.quantity} × {formatCurrency(item.unit_price)}
                            </span>
                            <span className="font-bold text-gray-700">
                              {formatCurrency(item.total || item.quantity * item.unit_price)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pricing Breakdown */}
                <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Pri Amazon:</span>
                    <span className="font-medium">{formatCurrency(totals.amazonSubtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[#FF9900]">
                    <span>Frè BVGLINKAMAZON ({(totals.fee_percent * 100).toFixed(0)}%):</span>
                    <span className="font-medium">+ {formatCurrency(totals.bvgpromoFee)}</span>
                  </div>

                  {totals.haiti_shipping_fee > 0 && (
                    <div className={`flex justify-between p-2 rounded ${totals.pay_now ? 'bg-green-50' : 'bg-gray-100 italic text-gray-500'}`}>
                      <span className="flex items-center gap-1 text-sm">
                        <Truck className="w-3 h-3" />
                        Shipping Ayiti:
                      </span>
                      <span className="text-sm font-bold">
                        {totals.pay_now ? `+ ${formatCurrency(totals.haiti_shipping_fee)}` : `(Peye Ayiti: ${formatCurrency(totals.haiti_shipping_fee)})`}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-lg font-bold border-t pt-3">
                    <span className="text-[#232F3E]">
                      <DollarSign className="w-5 h-5 inline mr-1 text-[#FF9900]" />
                      Total USD:
                    </span>
                    <span className="text-[#232F3E]">{formatCurrency(totals.totalUSD)}</span>
                  </div>
                  <div className="flex items-center justify-between text-2xl font-bold bg-[#FF9900]/10 p-3 rounded-lg">
                    <span className="text-[#232F3E]">Total HTG:</span>
                    <span className="text-[#FF9900]">{formatHTG(totals.totalHTG)}</span>
                  </div>
                  <p className="text-xs text-gray-500 text-right">
                    Taux: 1 USD = {totals.rate} HTG
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Shipping Selection Form (Only if order not created) */}
            {!order && cartData && (
              <Card className="border-0 shadow-lg overflow-hidden">
                <CardHeader className="bg-[#232F3E] text-white">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Truck className="w-5 h-5 text-[#FF9900]" />
                    Chwa Livrezon pou Ayiti
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-bold">Nimewo Konpayi Shipping</Label>
                      <select
                        className="w-full p-2 border rounded-md bg-white focus:ring-2 focus:ring-[#FF9900]"
                        value={selectedShippingCompanyId}
                        onChange={(e) => setSelectedShippingCompanyId(e.target.value)}
                      >
                        <option value="">-- Chwazi --</option>
                        {shippingCompanies.map(co => (
                          <option key={co.id} value={co.id}>
                            {co.name} (${co.price_per_lbs.toFixed(2)}/lbs)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold">Pwa estimatif (LBS)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="Ex: 5"
                        value={shippingLbs}
                        onChange={(e) => setShippingLbs(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <input
                      type="checkbox"
                      id="ent_pay_now"
                      checked={payShippingNow}
                      onChange={(e) => setPayShippingNow(e.target.checked)}
                      className="w-5 h-5 rounded accent-[#FF9900]"
                    />
                    <div className="flex-1">
                      <Label htmlFor="ent_pay_now" className="font-bold cursor-pointer block">Peye shipping nan kounye a</Label>
                      <p className="text-xs text-gray-500 mt-1">
                        {payShippingNow
                          ? "Y ap ajoute pri livrezon ayiti a nan fakti USD a."
                          : "W ap peye pri livrezon an lè machandiz la rive an Ayiti."}
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={() => createOrderFromCart(cartData)}
                    disabled={orderCreating || !selectedShippingCompanyId || shippingLbs <= 0}
                    className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6 text-lg"
                  >
                    {orderCreating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Konfime ak Kreye Kòmand"
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Approval Notice */}
            {order?.status === 'pending_approval' && (
              <Card className="border-0 shadow-lg border-l-4 border-l-yellow-500 bg-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-yellow-800">Ap Tann Apwobasyon</h3>
                      <p className="text-yellow-700 text-sm mt-1">
                        Kòmand sa a bezwen apwobasyon anvan li ka trete.
                        Ou ap resevwa yon notifikasyon lè li apwouve.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Section - Show when approved */}
            {canPay && !paymentInstructions && (
              <Card className="border-0 shadow-lg border-l-4 border-l-green-500 bg-green-50">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <CreditCard className="w-8 h-8 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-bold text-green-800">Chwazi Metòd Peman</h3>
                        <p className="text-green-700 text-sm mt-1">
                          Chwazi kijan ou vle peye pou kòmand sa a
                        </p>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setSelectedPaymentMethod('moncash')}
                        className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'moncash'
                          ? 'border-[#E31837] bg-[#E31837]/10'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                        data-testid="payment-moncash"
                      >
                        <CreditCard className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === 'moncash' ? 'text-[#E31837]' : 'text-gray-500'
                          }`} />
                        <p className={`font-bold text-center ${selectedPaymentMethod === 'moncash' ? 'text-[#E31837]' : 'text-gray-700'
                          }`}>MonCash</p>
                        <p className="text-xs text-gray-500 text-center mt-1">Peman otomatik</p>
                      </button>

                      <button
                        onClick={() => setSelectedPaymentMethod('virement')}
                        className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'virement'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                        data-testid="payment-virement"
                      >
                        <Building2 className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === 'virement' ? 'text-blue-500' : 'text-gray-500'
                          }`} />
                        <p className={`font-bold text-center ${selectedPaymentMethod === 'virement' ? 'text-blue-600' : 'text-gray-700'
                          }`}>Virement</p>
                        <p className="text-xs text-gray-500 text-center mt-1">Transfè bank</p>
                      </button>

                      <button
                        onClick={() => setSelectedPaymentMethod('zelle')}
                        className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'zelle'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                        data-testid="payment-zelle"
                      >
                        <Banknote className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === 'zelle' ? 'text-purple-500' : 'text-gray-500'
                          }`} />
                        <p className={`font-bold text-center ${selectedPaymentMethod === 'zelle' ? 'text-purple-600' : 'text-gray-700'
                          }`}>Zelle</p>
                        <p className="text-xs text-gray-500 text-center mt-1">USD sèlman</p>
                      </button>
                    </div>

                    {/* Amount to Pay */}
                    <div className="bg-white p-4 rounded-lg border border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Montan pou Peye:</span>
                        <div className="text-right">
                          {selectedPaymentMethod === 'zelle' ? (
                            <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalUSD)}</p>
                          ) : (
                            <>
                              <p className="text-2xl font-bold text-[#FF9900]">{formatHTG(totalHTG)}</p>
                              <p className="text-sm text-gray-500">≈ {formatCurrency(totalUSD)}</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pay Button */}
                    <Button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className={`w-full font-bold py-6 text-lg ${selectedPaymentMethod === 'moncash'
                        ? 'bg-[#E31837] hover:bg-[#C71530] text-white'
                        : selectedPaymentMethod === 'virement'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      data-testid="pay-btn"
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Chajman...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Peye ak {selectedPaymentMethod === 'moncash' ? 'MonCash' : selectedPaymentMethod === 'virement' ? 'Virement' : 'Zelle'}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Instructions (for Virement/Zelle) */}
            {paymentInstructions && (
              <Card className="border-0 shadow-lg border-l-4 border-l-blue-500 bg-blue-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-blue-800 mb-4">
                    Enstriksyon Peman {selectedPaymentMethod === 'virement' ? 'Virement' : 'Zelle'}
                  </h3>

                  <div className="bg-white rounded-lg p-4 space-y-3">
                    {selectedPaymentMethod === 'virement' && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Bank:</span>
                          <span className="font-bold">{paymentInstructions.bank_name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Non Kont:</span>
                          <span className="font-bold">{paymentInstructions.account_name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Nimewo Kont:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold">{paymentInstructions.account_number}</span>
                            <button onClick={() => copyToClipboard(paymentInstructions.account_number)}>
                              <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center bg-yellow-50 p-2 rounded">
                          <span className="text-gray-600">Referans:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#FF9900]">{paymentInstructions.reference}</span>
                            <button onClick={() => copyToClipboard(paymentInstructions.reference)}>
                              <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="font-bold">Montan:</span>
                          <span className="text-xl font-bold text-[#FF9900]">{formatHTG(paymentInstructions.amount_htg)}</span>
                        </div>
                      </>
                    )}

                    {selectedPaymentMethod === 'zelle' && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Imèl Zelle:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{paymentInstructions.email}</span>
                            <button onClick={() => copyToClipboard(paymentInstructions.email)}>
                              <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center bg-yellow-50 p-2 rounded">
                          <span className="text-gray-600">Referans (Memo):</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-purple-600">{paymentInstructions.reference}</span>
                            <button onClick={() => copyToClipboard(paymentInstructions.reference)}>
                              <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="font-bold">Montan:</span>
                          <span className="text-xl font-bold text-purple-600">{formatCurrency(paymentInstructions.amount_usd)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-sm text-blue-700 mt-4">
                    ⚠️ Enpòtan: Mete nimewo kòmand la kòm referans pou nou ka idantifye peman ou a.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payment Success */}
            {order?.status === 'paid' && (
              <Card className="border-0 shadow-lg border-l-4 border-l-blue-500 bg-blue-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-blue-800">Peman Konplete!</h3>
                      <p className="text-blue-700 text-sm mt-1">
                        Peman ou a resevwa. Kòmand ou a ap trete touswit.
                      </p>
                      {order?.payment?.transaction_id && (
                        <p className="text-xs text-blue-600 mt-2 font-mono">
                          ID Tranzaksyon: {order.payment.transaction_id}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/enterprise/dashboard')}
                className="flex-1 bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6"
                data-testid="go-to-dashboard"
              >
                <Home className="w-5 h-5 mr-2" />
                Ale nan Dashboard
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                className="flex-1 py-6"
                data-testid="print-order"
              >
                <Printer className="w-5 h-5 mr-2" />
                Enprime Kòmand
              </Button>
            </div>
          </>
        )}
      </div>
    </EnterpriseLayout>
  );
};

